import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuthedBlob } from "@/lib/useAuthedBlob";

export const Section = ({ id, title, desc, children }) => (
  <section id={id} className="scroll-mt-24 rounded-2xl border border-slate-200 bg-white p-6 md:p-8" data-testid={`settings-section-${id}`}>
    <h2 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>{title}</h2>
    {desc && <p className="text-sm text-slate-500 mt-1">{desc}</p>}
    <div className="mt-6">{children}</div>
  </section>
);

const Field = ({ id, label, value, onChange, placeholder, area, span }) => (
  <div className={span ? "md:col-span-2" : ""}>
    <Label htmlFor={id}>{label}</Label>
    {area
      ? <Textarea id={id} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={3} className="mt-1" data-testid={`settings-${id}`} />
      : <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1" data-testid={`settings-${id}`} />}
  </div>
);

export const LogoPicker = ({ api, hasLogo, onChanged }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [v, setV] = useState(0);
  const url = useAuthedBlob(hasLogo ? "/settings/logo" : null, v);
  const pick = async (f) => {
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(f.type)) { toast.error("Use a PNG, JPG or WEBP image"); return; }
    if (f.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setV((x) => x + 1);
      onChanged(true);
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Logo upload failed");
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => { await api.delete("/settings/logo"); onChanged(false); toast.success("Logo removed"); };
  return (
    <div className="flex items-center gap-4" data-testid="logo-picker">
      <div className="w-28 h-20 rounded-xl border border-dashed border-slate-300 bg-slate-50 grid place-items-center overflow-hidden">
        {url ? <img src={url} alt="Company logo" className="max-w-full max-h-full object-contain" data-testid="logo-preview" /> : <ImagePlus className="w-6 h-6 text-slate-400" />}
      </div>
      <div className="space-y-2">
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} data-testid="logo-input" />
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => ref.current?.click()} disabled={busy} data-testid="logo-upload-btn">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{hasLogo ? "Replace logo" : "Upload logo"}
          </Button>
          {hasLogo && <Button type="button" variant="ghost" size="sm" onClick={remove} className="text-rose-600 hover:bg-rose-50" data-testid="logo-remove-btn"><Trash2 className="w-4 h-4 mr-1" /> Remove</Button>}
        </div>
        <p className="text-xs text-slate-500">PNG, JPG or WEBP, up to 2 MB. Shown at the top of your invoices.</p>
      </div>
    </div>
  );
};

export const CompanyFields = ({ f, set }) => (
  <div className="grid md:grid-cols-2 gap-5">
    <Field id="name" label="Company name" value={f.name} onChange={set("name")} placeholder="Your trucking company LLC" span />
    <Field id="address" label="Address" value={f.address} onChange={set("address")} placeholder={"Street\nCity, ST ZIP"} area span />
    <Field id="phone" label="Phone" value={f.phone} onChange={set("phone")} placeholder="(555) 555-0100" />
    <Field id="email" label="Billing email" value={f.email} onChange={set("email")} placeholder="billing@yourcompany.com" />
    <Field id="mc_number" label="MC number" value={f.mc_number} onChange={set("mc_number")} placeholder="MC-123456" />
    <Field id="dot_number" label="DOT number" value={f.dot_number} onChange={set("dot_number")} placeholder="1234567" />
  </div>
);

export const RemitFields = ({ f, set, terms }) => (
  <div className="space-y-5">
    <div className="grid md:grid-cols-2 gap-5">
      <Field id="remit_direct" label="Direct: your bank / ACH details" value={f.remit_direct} onChange={set("remit_direct")} placeholder={"Bank name, routing and account number\nor mailing address for checks"} area />
      <Field id="remit_factored" label="Factored: factoring company remit-to" value={f.remit_factored} onChange={set("remit_factored")} placeholder={"Factoring company name\nRemit-to address or ACH details\nNotice of assignment wording"} area />
    </div>
    <div className="grid md:grid-cols-2 gap-5">
      <div>
        <Label>Default remit-to on new invoices</Label>
        <div className="mt-1 grid grid-cols-2 gap-2" role="radiogroup">
          {[["direct", "Direct"], ["factored", "Factored"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => set("default_remit")(k)} aria-pressed={f.default_remit === k}
              className={`h-10 rounded-lg border text-sm font-medium transition-colors ${f.default_remit === k ? "border-[#002FA7] bg-blue-50 text-[#002FA7]" : "border-slate-200 hover:bg-slate-50"}`}
              data-testid={`settings-default-remit-${k}`}>{l}</button>
          ))}
        </div>
      </div>
      <div>
        <Label htmlFor="default_terms">Default payment terms</Label>
        <select id="default_terms" value={f.default_terms} onChange={(e) => set("default_terms")(e.target.value)}
          className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm" data-testid="settings-default-terms">
          {terms.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </div>
    </div>
  </div>
);
